import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    SeasonApiError,
    isSeasonFeatureDisabled,
    isSeasonAuthExpired,
    getSeasonStandings,
} from '../api/seasonApi';
import { Button } from '../components/ui';
import './SeasonTeamPage.css';

/**
 * Season standings — backed by GET /api/v2/season-leagues/<id>/standings/.
 *
 * WHY THIS EXISTS RATHER THAN REUSING StandingsPage (2026-08-16, PR review):
 * the two modes compute standings from DIFFERENT DATA with DIFFERENT RULES, and
 * the weekly endpoint has no guard that would reject a season league — so a
 * season manager was shown a plausible, quietly WRONG table.
 *
 *   WEEKLY (LeagueStandingsView): ranks `Team.wins/losses/ties` and the CURRENT
 *     week's WeeklyTeamStats, substituting **0** when that row is absent. Sort:
 *     wins → losses → ties → score, hardcoded.
 *
 *   SEASON (compute_standings): ranks SEALED `SeasonTeamWeekResult` rows only,
 *     ordered by the tiebreak recipe PINNED to the league's rules version
 *     (H2H / POINTS_FOR / … — configurable per version, not hardcoded).
 *
 * Same-looking table, different answer. A wrong-but-plausible standing is worse
 * than an error screen: nothing tells the user it is wrong, and they make roster
 * decisions on it.
 *
 * `tiebreak_recipe_applied: false` means the league has no pinned recipe yet and
 * the backend fell back to a provisional WINS → POINTS_FOR ordering. That is
 * surfaced explicitly — presenting a provisional order as the real ranking is
 * the same class of lie in miniature.
 */
function SeasonStandingsPage({ leagueId, teamId, setPage }) {
    const [rows, setRows] = useState([]);
    const [tiebreakApplied, setTiebreakApplied] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [featureDisabled, setFeatureDisabled] = useState(false);
    const [authExpired, setAuthExpired] = useState(false);

    const isMountedRef = useRef(true);
    const requestIdRef = useRef(0);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        if (!leagueId) {
            throw new Error(
                `❌ SeasonStandingsPage: leagueId is required, got ${JSON.stringify(leagueId)}`
            );
        }

        const requestId = ++requestIdRef.current;
        setLoading(true);
        setLoadError(null);
        setFeatureDisabled(false);
        setAuthExpired(false);

        try {
            const payload = await getSeasonStandings(leagueId);
            if (!isMountedRef.current || requestIdRef.current !== requestId) {
                console.debug(
                    `🕒 SeasonStandingsPage: discarding superseded load (request #${requestId})`
                );
                return;
            }
            setRows(payload.standings);
            // Only `false` means provisional. An ABSENT flag is a contract break,
            // not permission to claim the ranking is official — so treat only an
            // explicit true as "recipe applied".
            setTiebreakApplied(payload.tiebreak_recipe_applied === true);
        } catch (error) {
            if (!isMountedRef.current || requestIdRef.current !== requestId) {
                return;
            }
            console.error('❌ SeasonStandingsPage: failed to load standings:', error);
            setFeatureDisabled(isSeasonFeatureDisabled(error));
            setAuthExpired(isSeasonAuthExpired(error));
            setLoadError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to load standings'
            );
        } finally {
            if (isMountedRef.current && requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [leagueId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleNavigation = (destination) => {
        switch (destination) {
            case 'LEAGUES':
                setPage('HOME');
                break;
            case 'TEAM':
                setPage('TEAM');
                break;
            case 'STANDINGS':
                // Already here
                break;
            default:
                throw new Error(
                    `❌ SeasonStandingsPage: unknown navigation destination: ${destination}`
                );
        }
    };

    if (loading) {
        return <div className="season-team-loading">Loading standings…</div>;
    }

    if (loadError) {
        return (
            <div className="season-team-error">
                <h2>
                    {featureDisabled
                        ? 'Season Campaign Unavailable'
                        : authExpired
                          ? 'Your Session Expired'
                          : 'Couldn’t Load Standings'}
                </h2>
                <p className="season-team-error-message">
                    {featureDisabled
                        ? 'Season-long campaigns are not enabled for your account yet.'
                        : authExpired
                          ? 'You’ve been signed out. Taking you back to the login screen…'
                          : loadError}
                </p>
                {/* No Retry for either terminal case — a blocked flag will not
                    change on retry, and an expired session cannot succeed. */}
                {!featureDisabled && !authExpired && (
                    <Button onClick={load} variant="secondary">
                        Retry
                    </Button>
                )}
                <Button onClick={() => setPage('TEAM')} variant="secondary">
                    Back to My Campaign
                </Button>
            </div>
        );
    }

    const numericTeamId = Number(teamId);

    return (
        <div className="season-team-page">
            <div className="season-tab-bar">
                <button className="season-tab" onClick={() => handleNavigation('LEAGUES')}>
                    LEAGUES
                </button>
                <button className="season-tab" onClick={() => handleNavigation('TEAM')}>
                    TEAM
                </button>
                <button
                    className="season-tab season-tab--active"
                    onClick={() => handleNavigation('STANDINGS')}
                >
                    STANDINGS
                </button>
            </div>

            {/* Standings move ONLY when a week is SEALED. Saying so up front
                stops a manager reading a mid-week table as final. */}
            <div className="season-week-strip" role="status">
                <span className="season-week-status">
                    Standings count SEALED weeks only
                </span>
                {!tiebreakApplied && (
                    <span className="season-week-provisional">
                        Provisional order — no tiebreak rules are pinned yet, so ties
                        are shown by wins then points for
                    </span>
                )}
            </div>

            <div className="season-roster-section">
                <div className="season-section-title-row">
                    <span className="season-section-title season-section-title--muted">
                        STANDINGS
                    </span>
                </div>
                <div className="season-card-stack">
                    {rows.length === 0 ? (
                        <div className="season-no-players">
                            No sealed results yet — standings appear once a week is sealed.
                        </div>
                    ) : (
                        rows.map((row) => (
                            <div
                                key={row.team_id}
                                className={[
                                    'season-standings-row',
                                    row.team_id === numericTeamId
                                        ? 'season-standings-row--mine'
                                        : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                <span className="season-standings-rank">{row.rank}</span>
                                <span className="season-standings-name">{row.team_name}</span>
                                <span className="season-standings-record">
                                    {row.wins}-{row.losses}-{row.ties}
                                </span>
                                <span className="season-standings-points">
                                    {row.points_for.toFixed(1)} PF
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="season-team-footer">
                <Button onClick={() => setPage('TEAM')} variant="secondary">
                    Back to My Campaign
                </Button>
            </div>
        </div>
    );
}

export default SeasonStandingsPage;
